import { BusinessHours } from '../interfaces/search/BusinessHours';
import {
  OpeningHours,
  OpeningHoursContainer,
  OpenTimes,
  ShortDayNames
} from '../interfaces/search/OpeningHours';
import { MemoItem } from '../interfaces/webservices/core-api-v2/Response/AddressSearches';

const week: string[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday'
];
export const shortDays: ShortDayNames[] = [
  'mo',
  'tu',
  'we',
  'th',
  'fr',
  'sa',
  'su'
];

function formatOpeningHours(
  day: ShortDayNames,
  from: string,
  until: string,
  exceptionDate?: string
): OpeningHours {
  return { day, from, until, exceptionDate };
}

function formatOpeningHoursLongSaturday(
  day: ShortDayNames,
  from: string,
  until: string
): OpeningHours {
  return { day, from, until, longSaturday: true };
}

function getTimeFromMask1(
  memoItem: any,
  day: string,
  index: number
): OpeningHours {
  if (day === 'Saturday' || day === 'Sunday') {
    return formatOpeningHours(
      shortDays[index],
      memoItem['OpeningHours' + day + 'From'],
      memoItem['OpeningHours' + day + 'Until']
    );
  }
  return formatOpeningHours(
    shortDays[index],
    memoItem.OpeningHoursMondayTilFridayFrom,
    memoItem.OpeningHoursMondayTilFridayUntil
  );
}

function getTime(now: number, hours: string, minutes: string): number {
  const date = new Date(now);
  date.setMilliseconds(0);
  date.setSeconds(0);
  date.setMinutes(parseInt(minutes, 10));
  date.setHours(parseInt(hours, 10));
  return date.getTime();
}

const normalizeMasks = [
  // MASK_1
  (memoItem: any, index: number, now: number): OpeningHoursContainer => {
    let day = week[now];
    let dayLower = day.toLowerCase();
    let time = getTimeFromMask1(memoItem, day, now);

    const today = [time];
    const openingHours: OpeningHours[] = [];
    for (let i = 0; i < 7; i++) {
      day = week[i];
      dayLower = day.toLowerCase();
      time = getTimeFromMask1(memoItem, day, i);
      openingHours.push(
        formatOpeningHours(shortDays[i], time.from || '', time.until)
      );
    }
    return { text: '', openingHours, today };
  },

  // MASK_2
  (memoItem: any, index: number, now: number): OpeningHoursContainer => {
    const day = week[index];
    const from = memoItem['OpeningHours' + day + 'From'];
    const until = memoItem['OpeningHours' + day + 'Until'];

    const today = [];
    const openingHours = [];
    if (from && until) {
      const openingHour = formatOpeningHours(shortDays[index], from, until);
      openingHours.push(openingHour);
      if (now === index + 1) {
        today.push(openingHour);
      }
    }

    return {
      text: '',
      openingHours,
      today
    };
  },

  // MASK_3
  // Wird gleich behandelt, wie Maske 2. Einziger Unterschied: Es berücksichtigt lange Samstage.
  (memoItem: any, index: number, now: number): OpeningHoursContainer => {
    const day = week[index];
    const from = memoItem['OpeningHours' + day + 'From'];
    const until = memoItem['OpeningHours' + day + 'Until'];
    const longFrom = memoItem['OpeningHoursLong' + day + 'From'];
    const longUntil = memoItem['OpeningHoursLong' + day + 'Until'];

    const today = [];
    const openingHours = [];
    if (from && until) {
      const openingHour = formatOpeningHours(shortDays[index], from, until);
      const longSaturday =
        day === 'Saturday' &&
        longFrom &&
        longUntil &&
        formatOpeningHoursLongSaturday(shortDays[index], longFrom, longUntil);

      openingHours.push(openingHour);
      if (longSaturday) {
        openingHours.push(longSaturday);
      }
      if (now === index + 1) {
        today.push(openingHour);
        if (longSaturday) {
          today.push(longSaturday);
        }
      }
    }

    return {
      text: '',
      openingHours,
      today
    };
  },

  // MASK_4
  (memoItem: any, index: number, now: number): OpeningHoursContainer => {
    const day = week[index];
    const morningFrom = memoItem['OpeningHours' + day + 'MorningFrom'];
    const morningUntil = memoItem['OpeningHours' + day + 'MorningUntil'];
    const afternoonFrom = memoItem['OpeningHours' + day + 'AfternoonFrom'];
    const afternoonUntil = memoItem['OpeningHours' + day + 'AfternoonUntil'];

    const openingHours = [];
    const today = [];

    const morning =
      morningFrom &&
      morningUntil &&
      formatOpeningHours(shortDays[index], morningFrom, morningUntil);
    const afternoon =
      afternoonFrom &&
      afternoonUntil &&
      formatOpeningHours(shortDays[index], afternoonFrom, afternoonUntil);

    if (morning) {
      openingHours.push(morning);
    }
    if (afternoon) {
      openingHours.push(afternoon);
    }
    if (now === index + 1) {
      if (morning) {
        today.push(morning);
      }
      if (afternoon) {
        today.push(afternoon);
      }
    }

    return {
      text: '',
      openingHours,
      today
    };
  },

  // MASK_5
  (memoItem: any, index: number, now: number): OpeningHoursContainer => {
    const searchable = memoItem.SearchableOpeningHours;
    const l = searchable.length;
    let day;
    let from;
    let until;
    let exception;
    let i;

    const today = [];
    const openingHours = [];

    for (i = 0; i < l; i++) {
      day = +searchable[i].Day;
      from = searchable[i].From;
      until = searchable[i].Until;
      exception = searchable[i].ExceptionDate;

      const openingHour = formatOpeningHours(
        shortDays[i],
        from,
        until,
        exception
      );
      if (openingHour) {
        openingHours.push(openingHour);
      }
      if (now - 1 === day) {
        if (openingHour) {
          today.push(openingHour);
        }
      }
    }

    return {
      text: '',
      openingHours,
      today
    };
  }
];

/**
 * normalize an opening hours entry in a generic JSON format
 * @param memoItem
 * @param now
 */
export function normalizeOpeningHourEntry(
  memoItem: any,
  now: number
): OpeningHoursContainer {
  let index;
  let mask;
  let j;
  let normalize;
  if (!now) {
    now = new Date().getDay();
  }

  const container: OpeningHoursContainer = {
    text: '',
    openingHours: [],
    today: []
  };

  if (memoItem.Type === 'OPENING_HOURS' || memoItem.Type === 'OPENING_HOURS2') {
    mask = memoItem.mask = +memoItem.Title;
    index = memoItem.mask - 1;
    if (!isNaN(index) && isFinite(index) && index > -1) {
      normalize = normalizeMasks[index];
      if (mask === 1 || mask === 5) {
        const result = normalize(memoItem, -1, now);
        container.openingHours.push.apply(
          container.openingHours,
          result.openingHours
        );
        container.today.push.apply(container.today, result.today);
      } else {
        for (j = 0; j < 7; j++) {
          const result = normalize(memoItem, j, now);
          container.openingHours.push.apply(
            container.openingHours,
            result.openingHours
          );
          container.today.push.apply(container.today, result.today);
        }
      }
    } else if (memoItem.OpeningHoursMask === 'NONE') {
      container.text = memoItem.Text;
    }
  }
  return container;
}

/**
 * normalize multiple opening hours entries in a generic JSON format
 * @param memoItems
 * @param timeDelimiter
 * @returns {*}
 */
export function normalizeOpeningHours(
  memoItems: MemoItem[],
  timeDelimiter = /[.:-]/
): BusinessHours {
  const now = Date.now();
  const dayIndex = new Date(now).getDay();

  const businessHours: BusinessHours = {
    text: '',
    openAt: [],
    isOpen: false
  };

  for (const item of memoItems) {
    const normalized = normalizeOpeningHourEntry(item, dayIndex);
    if (normalized.text.length > 0 && businessHours.text.length === 0) {
      businessHours.text = normalized.text;
    } else if (normalized.openingHours.length) {
      businessHours.openAt.push(normalized);
    }
  }

  if (businessHours.openAt.length) {
    // first opening hours container is important for isOpen flag
    const [container] = businessHours.openAt;
    for (const { from, until, longSaturday } of container.today) {
      if (longSaturday) {
        // ignore this!
        continue;
      }
      const [fromHours, fromMinutes] = from.split(timeDelimiter);
      const [untilHours, untilMinutes] = until.split(timeDelimiter);
      const fromTime = getTime(now, fromHours, fromMinutes);
      const untilTime = getTime(now, untilHours, untilMinutes);
      businessHours.isOpen = now > fromTime && now < untilTime;
      if (businessHours.isOpen) {
        break;
      }
    }
  }
  return businessHours;
}

export function getOpenTimesFor(
  day: ShortDayNames,
  openingHours: OpeningHoursContainer
): OpenTimes {
  const openTime = { day, times: [] } as OpenTimes;
  openTime.times.push.apply(
    openTime.times,
    openingHours.openingHours.filter(openingHour => openingHour.day === day)
  );
  return openTime;
}
